import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Award, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { DjayAvatar } from '../../mascot/DjayAvatar';
import { useDjaySelection } from '../../mascot/useDjaySelection';
import { getAchievementIllustration } from '../../mascot/illustrations';
import { DjayIllustrationBase } from '../../mascot/illustrations/DjayIllustrationBase';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
import { soundManager } from '../../utils/soundManager';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
export interface AchievementData {
  id: string;
  name: string;
  description: string;
  emoji: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
}
interface AchievementUnlockModalProps {
  achievement: AchievementData | null;
  onClose: () => void;
}
export function AchievementUnlockModal({
  achievement,
  onClose
}: AchievementUnlockModalProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant
  } = useDjaySelection();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (achievement) {
      soundManager.playAchievement();
      setCopied(false);
    }
  }, [achievement]);
  if (!achievement) return null;
  const rarityColors = {
    common: 'from-slate-200 to-slate-300 text-slate-700 border-slate-300',
    uncommon: 'from-emerald-200 to-emerald-300 text-emerald-800 border-emerald-300',
    rare: 'from-blue-300 to-indigo-400 text-white border-blue-400',
    legendary: 'from-amber-300 via-orange-400 to-rose-400 text-white border-amber-400'
  };
  const rarityLabels = {
    common: 'Gewoon',
    uncommon: 'Bijzonder',
    rare: 'Zeldzaam',
    legendary: 'Legendarisch!'
  };
  const Illustration = achievement ? getAchievementIllustration(achievement.id) : null;
  return <AnimatePresence data-id="element-2029">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-2030">
        {/* Backdrop */}
        <motion.div initial={shouldAnimate ? {
        opacity: 0
      } : false} animate={shouldAnimate ? {
        opacity: 1
      } : false} exit={shouldAnimate ? {
        opacity: 0
      } : false} onClick={onClose} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" data-id="element-2031" />

        {/* Confetti */}
        <div className="absolute inset-0 pointer-events-none z-0" data-id="element-2032">
          <ConfettiCelebration data-id="element-2033" />
        </div>

        {/* Modal Content */}
        <motion.div initial={shouldAnimate ? {
        opacity: 0,
        scale: 0.8,
        y: 20
      } : false} animate={shouldAnimate ? {
        opacity: 1,
        scale: 1,
        y: 0
      } : false} exit={shouldAnimate ? {
        opacity: 0,
        scale: 0.9,
        y: -20
      } : false} transition={{
        type: 'spring',
        bounce: 0.5,
        duration: 0.6
      }} className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-2034">
          {/* Header Pattern */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 opacity-10" data-id="element-2035" />

          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-20" aria-label="Sluiten" data-id="element-2036">
            <X className="w-5 h-5" data-id="element-2037" />
          </button>

          <div className="p-8 flex flex-col items-center text-center relative z-10" data-id="element-2038">
            {/* Djay Avatar */}
            <div className="w-24 h-24 mb-6 relative" data-id="element-2039">
              <motion.div animate={shouldAnimate ? {
              y: [0, -10, 0]
            } : false} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-2040">
                <DjayAvatar emotion="celebrating" variant={selectedVariant} size="xl" animate={shouldAnimate} data-id="element-2041" />
              </motion.div>
            </div>

            <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-2042">
              Nieuwe Prestatie!
            </h2>

            {/* Illustration or Badge */}
            {Illustration ? <motion.div initial={shouldAnimate ? {
            scale: 0,
            rotate: -5
          } : false} animate={shouldAnimate ? {
            scale: 1,
            rotate: 0
          } : false} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className="w-full aspect-video rounded-2xl overflow-hidden mb-6 shadow-xl border-4 border-white relative" data-id="element-2043">
                <DjayIllustrationBase isUnlocked={true} rarity={achievement.rarity} data-id="element-2044">
                  <Illustration data-id="element-2045" />
                </DjayIllustrationBase>
              </motion.div> : <motion.div initial={shouldAnimate ? {
            scale: 0,
            rotate: -180
          } : false} animate={shouldAnimate ? {
            scale: 1,
            rotate: 0
          } : false} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${rarityColors[achievement.rarity]} border-4 shadow-xl flex items-center justify-center text-5xl mb-6 relative overflow-hidden`} data-id="element-2046">
                {/* Shine effect */}
                {shouldAnimate && <motion.div animate={{
              x: ['-100%', '200%']
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-1/2 bg-white/30 skew-x-12" data-id="element-2047" />}
                <span className="drop-shadow-md" data-id="element-2048">{achievement.emoji}</span>
              </motion.div>}

            <h3 className="text-2xl font-black text-slate-800 mb-2" data-id="element-2049">
              {achievement.name}
            </h3>
            <p className="text-slate-600 mb-4" data-id="element-2050">{achievement.description}</p>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-wider mb-8" data-id="element-2051">
              <Award className="w-3.5 h-3.5" data-id="element-2052" />
              {rarityLabels[achievement.rarity]}
            </div>

            <div className="w-full flex flex-col gap-3" data-id="element-2053">
              <Button onClick={onClose} size="lg" className="w-full font-bold" data-id="element-2054">
                Geweldig!
              </Button>
              <Button variant="outline" className={`w-full ${copied ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => {
              const text = `🏆 Ik heb de prestatie "${achievement.name}" behaald in Meester Djay! ${achievement.emoji}`;
              navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }).catch(() => {
                // Fallback for older browsers
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }} data-id="element-2055">
                {copied ? <>
                    <Check className="w-4 h-4 mr-2" data-id="element-2056" />
                    Gekopieerd!
                  </> : <>
                    <Share2 className="w-4 h-4 mr-2" data-id="element-2057" />
                    Deel met leerkracht
                  </>}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}